/**
 * AC-M4-2 — Frontend Sentry initialisation + captureException wrapper.
 *
 * Uses @sentry/react v7 to match the backend @sentry/node v7.
 * When VITE_SENTRY_DSN is not set, all calls are silent no-ops — the code
 * path is verified in tests by spying on captureException, not by requiring
 * a live DSN.
 *
 * Usage:
 *   import { initSentry, captureException } from '@/lib/sentry';
 *   initSentry();                     // once at app startup
 *   captureException(new Error('…')); // anywhere in the app
 */

import * as Sentry from '@sentry/react';

let _initialized = false;

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn || _initialized) return;

  Sentry.init({
    dsn,
    tracesSampleRate: 0.1,
    environment: import.meta.env.MODE ?? 'development',
    // Never include raw request bodies or PII in events
    sendDefaultPii: false,
    beforeSend(event) {
      // Strip any accidentally-attached credentials from breadcrumbs
      if (event.breadcrumbs) {
        const values = (event.breadcrumbs as any).values;
        if (Array.isArray(values)) {
          (event.breadcrumbs as any).values = values.map((b: any) => {
            if (b.data?.token || b.data?.access_token) {
              return { ...b, data: { ...b.data, token: '[REDACTED]', access_token: '[REDACTED]' } };
            }
            return b;
          });
        }
      }
      return event;
    },
  });

  _initialized = true;
}

/**
 * Capture an exception in Sentry.
 * Safe to call when DSN is not configured — Sentry silently no-ops.
 */
export function captureException(err: unknown, context?: Record<string, unknown>): void {
  if (context) {
    Sentry.captureException(err, { extra: context });
  } else {
    Sentry.captureException(err);
  }
}

/**
 * Expose the underlying Sentry instance for testing (spy injection).
 * Returns the @sentry/react module object.
 */
export { Sentry };
